// Theme preference: light | dark | system. The resolved theme is stamped on
// <html data-theme> (boot script in index.html does it pre-paint; this module
// keeps it in sync afterwards). Tailwind's darkMode selector and the CSS
// variable ramps in index.css read that attribute.

import { useSyncExternalStore } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';
const KEY = 'oc-theme';
const media = window.matchMedia('(prefers-color-scheme: light)');
const listeners = new Set<() => void>();

export function getPref(): ThemePref {
  const t = localStorage.getItem(KEY);
  return t === 'light' || t === 'dark' ? t : 'system';
}

function resolved(pref: ThemePref): 'light' | 'dark' {
  return pref === 'system' ? (media.matches ? 'light' : 'dark') : pref;
}

function apply(): void {
  document.documentElement.dataset.theme = resolved(getPref());
  listeners.forEach((l) => l());
}

export function setPref(pref: ThemePref): void {
  if (pref === 'system') localStorage.removeItem(KEY);
  else localStorage.setItem(KEY, pref);
  apply();
}

media.addEventListener('change', () => {
  if (getPref() === 'system') apply();
});

/** Current preference, re-rendering on change. */
export function useThemePref(): ThemePref {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getPref,
  );
}
